import {Debugger} from "debug";
import {AfterViewInit, booleanAttribute, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, Inject, InjectionToken, Input, Output, signal, ViewChild} from "@angular/core";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatInputModule} from "@angular/material/input";
import {MatAutocompleteModule} from "@angular/material/autocomplete";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {KlokkeslettVelgerKomponent} from "web-ng2/delt/komponenter/klokkeslett-velger/klokkeslett-velger";
import {VarighetVelgerKomponent} from "web-ng2/delt/komponenter/varighet-velger/varighet-velger";
import {Temporal} from "temporal-polyfill";
import {MatRadioModule} from "@angular/material/radio";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {NgStyle} from "@angular/common";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {MatIconModule} from "@angular/material/icon";
import {MøteDato, OppdaterMøteValideringsfeil, OpprettMøteValideringsfeil} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {debounceTime, filter, map, startWith, tap} from "rxjs";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {inneholderFeil} from "felles-ng2/støttefunksjoner/valideringsresultatModellHjelpere";
import {TypetValideringsresultatModell} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";
import {TranslateModule} from "@ngx-translate/core";
import {leggTilFeilDersom} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {ZonedDateTimePeriodeLukket} from "felles-ng2/typer/ZonedDateTimePeriode";
import {GjeldendeTidssone, Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import PlainDate = Temporal.PlainDate;
import PlainTime = Temporal.PlainTime;

const debug: Debugger = require('debug')('MøteRedigerTidspunktKontroller');

/**  InjectionToken for injisering av {@link MøteRedigerTidspunktKontrollerAvhengigheter} implementasjon. */
export const MøteRedigerTidspunktKontrollerAvhengigheter_DI_TOKEN = new InjectionToken<MøteRedigerTidspunktKontrollerAvhengigheter>('MøteRedigerTidspunktKontrollerAvhengigheter implementasjon');

export type SkjemaType = ReturnType<typeof MøteRedigerTidspunktKontroller.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];

@Component({
    selector: 'moete-rediger-tidspunkt',
    templateUrl: 'møte-rediger-tidspunkt.html',
    styleUrls: ['møte-rediger-tidspunkt.scss'],
    imports: [
        FormsModule,
        MatInputModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        MatDatepickerModule,
        KlokkeslettVelgerKomponent,
        VarighetVelgerKomponent,
        MatRadioModule,
        MatCheckboxModule,
        MatIconModule,
        FormaterPlainDatePipe,
        StandardValideringsfeilPipe,
        TipsKomponent,
        NgStyle,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteRedigerTidspunktKontroller implements AfterViewInit {

    static opprettSkjema() {
        return new FormGroup({
            startdato: new FormControl<PlainDate | null>(null, {validators: [Validators.required]}),
            startklokkeslett: new FormControl<PlainTime | null>(null, {validators: [Validators.required]}),
            sluttdato: new FormControl<PlainDate | null>(null, {validators: [Validators.required]}),
            sluttklokkeslett: new FormControl<PlainTime | null>(null, {validators: [Validators.required]}),
            flerdags: new FormControl(false),
        })
    }

    /** Returnerer tidspunktene som {@link ZonedDateTimePeriodeLukket}, forutsetter at skjemaet er gyldig. */
    static somMøteDato2(skjema: SkjemaVerdier, tidssone: Tidssoneinformasjon): ZonedDateTimePeriodeLukket {
        const sluttdato = skjema.flerdags ? skjema.sluttdato! : skjema.startdato!;
        return new ZonedDateTimePeriodeLukket(
          skjema.startdato!.toZonedDateTime({timeZone: tidssone.somZoneId(), plainTime: skjema.startklokkeslett!}),
          sluttdato.toZonedDateTime({timeZone: tidssone.somZoneId(), plainTime: skjema.sluttklokkeslett!}),
        );
    }

    /** Returnerer tidspunktene formatert som {@link MøteDato}, forutsetter at skjemaet er gyldig. */
    static somSkjemaverdier(møtedato: ZonedDateTimePeriodeLukket, tidssone: string): SkjemaVerdier {
        const start = møtedato.start.withTimeZone(tidssone);
        const slutt = møtedato.slutt.withTimeZone(tidssone);
        return {
            startdato: start.toPlainDate(),
            startklokkeslett: start.toPlainTime(),
            sluttdato: slutt.toPlainDate(),
            sluttklokkeslett: slutt.toPlainTime(),
            flerdags: !start.toPlainDate().equals(slutt.toPlainDate())
        }
    }

    /** Referanse til startdato-tekstfelt */
    @ViewChild('startdatofelt') startdatofelt!: ElementRef<HTMLInputElement>;
    /** Referanse til sluttdato-tekstfelt */
    @ViewChild('sluttdatofelt', {static: false}) sluttdatofelt: ElementRef<HTMLInputElement> | null = null;

    visIngenPlanMelding = signal(false);
    sisteGyldigStartklokkeslett = signal<PlainTime | null>(null);

    private _skjema!: SkjemaType;
    private _fokuserStartdatoFelt = false;

    constructor(
      @Inject(MøteRedigerTidspunktKontrollerAvhengigheter_DI_TOKEN) private avhengigheter: MøteRedigerTidspunktKontrollerAvhengigheter,
      private destroyRef: DestroyRef, private gjeldendeTidssone: GjeldendeTidssone) {
    }

    /** publiserer event når bruker klikker på "opprett ny årsplan" i tips som vises dersom valgt dato ikke dekkes av noen eksisterende årsplan */
    @Output() vedOpprettAarsplan = new EventEmitter<{ årstall: number }>();

    get skjema() {
        return this._skjema;
    }

    @Input({required: true}) set skjema(skjema: SkjemaType) {
        debug('skjema', skjema)
        this._skjema = skjema;
        this.håndterEndringerFlerdagsmøte();
        this.oppdaterSisteGyldigStartklokkeslett();
        this.validerVedEndringer();
    }

    @Input({transform: booleanAttribute}) set fokuserStartdatoFelt(fokuser: boolean) {
        debug('fokuserStartdatoFelt', fokuser)
        this._fokuserStartdatoFelt = fokuser;
        this.giStartdatofeltFokus();
    }

    get fokuserStartdatoFelt() {
        return this._fokuserStartdatoFelt;
    }

    opprettAarsplan() {
        const dato = this.skjema.value.startdato!
        this.vedOpprettAarsplan.emit({årstall: dato.year});
        this.avhengigheter.opprettNyÅrsplan(dato.year);
    }

    ngAfterViewInit() {
        this.giStartdatofeltFokus();
    }

    //private hjelpemetoder

    private giStartdatofeltFokus() {
        debug('giStartdatofeltFokus', this.fokuserStartdatoFelt, erIkkeNil(this.startdatofelt))
        if (this.fokuserStartdatoFelt && erIkkeNil(this.startdatofelt))
          //uten timeout rendrer ikke mat-form-field flytende label korrekt
            window.setTimeout(() => {
                debug('giStartdatofeltFokus posttimeout')
                this.startdatofelt.nativeElement.focus()
            }, 200);
    }

    private giSluttdatofeltFokus() {
        //timeout for at sluttdatofelt skal ha rukket å rendre
        window.setTimeout(() => this.sluttdatofelt!.nativeElement.focus(), 200);
    }

    /** aktiver/deaktiver sluttdato i tråd med flerdagsmøte-valget */
    private håndterEndringerFlerdagsmøte() {
        const flerdagsKtr = this.skjema.controls.flerdags;
        const sluttdatoKtr = this.skjema.controls.sluttdato;
        if (!flerdagsKtr.value) sluttdatoKtr.disable();
        flerdagsKtr.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(flerdags => {
              if (flerdags) {
                  sluttdatoKtr.enable();
                  this.giSluttdatofeltFokus();

              } else
                  sluttdatoKtr.disable();
          });
    }

    private oppdaterSisteGyldigStartklokkeslett() {
        return this.skjema.controls.startklokkeslett.valueChanges
          .pipe(
            startWith(this.skjema.controls.startklokkeslett.value),
            takeUntilDestroyed(this.destroyRef),
            filter(erIkkeNil)
          )
          .subscribe(startklokkeslett => this.sisteGyldigStartklokkeslett.set(startklokkeslett));
    }

    /** Valider møtetidspunkt når alle felter er fylt ut */
    private validerVedEndringer() {
        this.skjema.valueChanges
          .pipe(
            filter(skjemaverdier => this.alleFelterUtfylt(skjemaverdier)),
            map(skjemaverdier => MøteRedigerTidspunktKontroller.somMøteDato2(skjemaverdier, this.gjeldendeTidssone.brukersTidssone())),
            //registrer valideringsfeil på startdatofelt
            tap(møtedato => this.leggTilFeilPåStartdatofeltDersom(!møtedato.sluttErEtterStart(), 'startEtterSlutt')),
            //fortsett med servervalidering dersom møtedato ok
            filter(møtedato => møtedato.sluttErEtterStart()),
            debounceTime(VarighetVelgerKomponent.undertrykkForskyvningVindu + 50),
            takeUntilDestroyed(this.destroyRef),
          ).subscribe(møtedato => this.kjørServervalidering(møtedato));
    }

    /** Gjør async kall mot tjeneste som kan validere møtetidspunkt.
     * På dette tidspunkt vil alle skjemafelter være fylt ut og skjema er markert som gyldig. */
    private async kjørServervalidering(møtedato: ZonedDateTimePeriodeLukket) {
        debug('kaller avhengigheter.validerMøtetidspunkt med %j', møtedato)

        const resultat = await this.avhengigheter.validerMøtetidspunkt(møtedato);
        debug('avhengigheter.validerMøtetidspunkt resultat: %j', resultat)

        //OppdaterMøteValideringsfeil er et rent subset av OpprettMøteValideringsfeil - tester derfor kun mot OpprettMøteValideringsfeil
        this.leggTilFeilPåStartdatofeltDersom(inneholderFeil(resultat, OpprettMøteValideringsfeil.protokoll_låst), 'protokollLaast');
        this.leggTilFeilPåStartdatofeltDersom(inneholderFeil(resultat, OpprettMøteValideringsfeil.møte_opprett_samme_starttidspunkt), 'overlapper');

        this.visIngenPlanMelding.set(inneholderFeil(resultat, OpprettMøteValideringsfeil.møte_opprett_ingen_plan));
    }

    /** legger angitt feil til eventuelle eksisterende feil for startdato-feltet */
    private leggTilFeilPåStartdatofeltDersom(betingelse: boolean, nøkkel: string) {
        leggTilFeilDersom(betingelse, nøkkel, this.skjema.controls.startdato);
        this.skjema.controls.startdato.markAsTouched();//uten denne vises ikke feilmeldinger med mindre bruker har vært innom dette feltet
    }

    private alleFelterUtfylt(skjemaverdier: typeof this.skjema.value) {
        if (erNil(skjemaverdier.startdato) || erNil(skjemaverdier.startklokkeslett) || erNil(skjemaverdier.sluttklokkeslett))
            return false;
        if (skjemaverdier.flerdags && erNil(skjemaverdier.sluttdato))
            return false;
        return true;
    }
}

export type MøtetidspunktValideringsfeil = OpprettMøteValideringsfeil | OppdaterMøteValideringsfeil;

export interface MøteRedigerTidspunktKontrollerAvhengigheter {
    /** Validerer møtedato vhja {@link MøteRessursClient#validerNyttMøte} / {@link MøteRessursClient#validerOppdater} */
    validerMøtetidspunkt(møtedato: ZonedDateTimePeriodeLukket): Promise<TypetValideringsresultatModell<MøtetidspunktValideringsfeil>>;

    /** Viser bruker dialog for opprettelse av ny årsplan.
     * Merk at denne kun er aktuell ved opprettelse av nytt møte (via OpprettMøteValideringsfeil.møte_opprett_ingen_plan) */
    opprettNyÅrsplan(årstall: number): void;
}
